import { useCallback } from 'react';

import type { ExportOptions } from './invoice.slice';
import {
  setExportFlowStep,
  setExportInvoiceIds,
  setExportInvoicesReportType,
  setInvoiceExportMode,
  setShowExportInvoicesModal,
} from './invoice.slice';
import { useLazyFetchInvoiceExportData } from './modals/DownloadInvoices/useLazyFetchInvoiceExportData';
import { useAppDispatch } from 'src/app/store/hooks';

// Unified handler to export invoices by invoice id
export function useExportInvoices(
  exportMode: 'BLOB' | 'DOWNLOAD' = 'DOWNLOAD'
) {
  const { trigger, loading } = useLazyFetchInvoiceExportData();
  const dispatch = useAppDispatch();

  const handleExportInvoiceAsQuickbooksCSV = useCallback(
    async (invoices: string[]) => {
      await trigger({
        selectedInvoices: invoices,
        format: 'csv',
      });
    },
    [trigger]
  );

  const handleExportInvoiceAsCondensedPDF = useCallback(
    async (invoices: string[]) => {
      await trigger({
        selectedInvoices: invoices,
        format: 'pdf',
        outputType: 'condensed',
      });

      dispatch(setExportInvoicesReportType('condensed'));
      dispatch(setExportFlowStep('skipToGeneration'));
      dispatch(setShowExportInvoicesModal(true));
      dispatch(setInvoiceExportMode(exportMode));
    },
    [dispatch, exportMode, trigger]
  );

  const handleExport = useCallback(
    async (type: ExportOptions, invoices: string[]) => {
      if (type === 'quickbooks') {
        await handleExportInvoiceAsQuickbooksCSV(invoices);
      } else {
        await handleExportInvoiceAsCondensedPDF(invoices);
      }
    },
    [handleExportInvoiceAsCondensedPDF, handleExportInvoiceAsQuickbooksCSV]
  );

  // Like handleExport, but for condensed PDFs it opens the export modal with
  // a sort-order step first instead of fetching immediately, so the user can
  // choose how the PDF is ordered before we generate it.
  const handleExportWithSortPrompt = useCallback(
    async (type: ExportOptions, invoices: string[]) => {
      if (type === 'quickbooks') {
        await handleExportInvoiceAsQuickbooksCSV(invoices);
        return;
      }

      dispatch(setExportInvoiceIds(invoices));
      dispatch(setExportInvoicesReportType('condensed'));
      dispatch(setExportFlowStep('promptSort'));
      dispatch(setShowExportInvoicesModal(true));
      dispatch(setInvoiceExportMode(exportMode));
    },
    [dispatch, exportMode, handleExportInvoiceAsQuickbooksCSV]
  );

  return {
    handleExport,
    handleExportWithSortPrompt,
    isLoading: loading,
  };
}
